import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import './demo2.css';
import {
    INITIAL_ALERTS, INITIAL_WORKORDERS, INITIAL_WORK_HISTORY, ZONES, findZone,
    calcCentroid, calcPolygonAreaM2, moistStatus, treeEmoji,
    type AlertItem, type WorkOrder, type WorkHistoryItem, type WorkStatus,
    type Zone, type ZoneCategory, type LatLng, type MonitorType,
} from './demo2Data';

export type ToastType = 'success' | 'error' | 'warning';
interface ToastItem { id: number; msg: string; type: ToastType; }

interface PanelState { title: string; body: ReactNode; footer?: ReactNode; }

interface Demo2Ctx {
    toasts: ToastItem[];
    toast: (msg: string, type?: ToastType) => void;
    panel: PanelState | null;
    openPanel: (p: PanelState) => void;
    closePanel: () => void;

    alerts: AlertItem[];
    markAlertRead: (id: string) => void;
    markAllAlertsRead: () => void;

    zones: Zone[];
    /** 지도에서 클릭으로 그린 폴리곤으로 신규 관리구역을 등록 */
    addZone: (input: { name: string; category: ZoneCategory; monitorType: MonitorType; mgr: string; treeName: string; treeCount: number; soilType: string; ph: number; drainage: '양호' | '보통' | '불량'; polygon: LatLng[] }) => string;

    workOrders: WorkOrder[];
    createWorkOrder: (input: { zone: string; type: WorkOrder['type']; assignee: string; due: string; priority: WorkOrder['priority']; memo?: string }) => string;
    /** 작업관리 칸반 카드 클릭 → 수정폼에서 대상구역/작업종류/우선순위/작업자/예정일을 변경 */
    updateWorkOrder: (id: string, input: { zone: string; type: WorkOrder['type']; assignee: string; due: string; priority: WorkOrder['priority'] }) => void;
    startWorkOrder: (id: string) => void;
    completeWorkOrder: (id: string, resultNote: string, amt?: string, mins?: string) => void;
    /** 칸반 드래그앤드롭 등으로 완료 처리 없이 상태만 직접 옮길 때 사용(대기 ↔ 진행중) */
    moveWorkOrderStatus: (id: string, status: WorkStatus) => void;

    workHistory: WorkHistoryItem[];
}

const Ctx = createContext<Demo2Ctx>({} as Demo2Ctx);
export const useDemo2Ctx = () => useContext(Ctx);

const ICON: Record<ToastType, string> = { success: '✓', error: '✕', warning: '⚠' };

export function Demo2Provider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const [panel, setPanel] = useState<PanelState | null>(null);
    const [alerts, setAlerts] = useState<AlertItem[]>(INITIAL_ALERTS);
    const [zones, setZones] = useState<Zone[]>(ZONES);
    const [workOrders, setWorkOrders] = useState<WorkOrder[]>(INITIAL_WORKORDERS);
    const [workHistory, setWorkHistory] = useState<WorkHistoryItem[]>(INITIAL_WORK_HISTORY);
    const idRef = useRef(0);
    const woSeqRef = useRef(3051);
    const zoneSeqRef = useRef(ZONES.length);
    const panelOpenedAtRef = useRef(0);

    const toast = useCallback((msg: string, type: ToastType = 'success') => {
        const id = ++idRef.current;
        setToasts(p => [...p, { id, msg, type }]);
        setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 2800);
    }, []);

    const openPanel = useCallback((p: PanelState) => {
        panelOpenedAtRef.current = Date.now();
        setPanel(p);
    }, []);
    const closePanel = useCallback(() => setPanel(null), []);
    /* 패널을 연 직후(300ms 이내) 오버레이 클릭으로 즉시 닫히는 것을 방지 —
     * 지도 그리기 완성처럼 패널을 여는 그 클릭이 오버레이까지 겹쳐 들어와
     * 열리자마자 닫히는 것처럼 보이는 문제의 안전장치. */
    const closePanelFromOverlay = useCallback(() => {
        if (Date.now() - panelOpenedAtRef.current < 300) return;
        setPanel(null);
    }, []);

    const markAlertRead = useCallback((id: string) => {
        setAlerts(prev => prev.map(a => (a.id === id ? { ...a, read: true } : a)));
    }, []);
    const markAllAlertsRead = useCallback(() => {
        setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    }, []);

    const createWorkOrder = useCallback((input: { zone: string; type: WorkOrder['type']; assignee: string; due: string; priority: WorkOrder['priority']; memo?: string }) => {
        const z = zones.find(zz => zz.id === input.zone) ?? findZone(input.zone);
        const id = `WO-${++woSeqRef.current}`;
        const wo: WorkOrder = {
            id, zone: input.zone, zoneName: z ? z.name : '-', type: input.type,
            title: `${z ? z.name : ''} ${input.type} 작업지시`, assignee: input.assignee,
            due: input.due, status: 'waiting', priority: input.priority, createdFrom: null,
        };
        setWorkOrders(prev => [wo, ...prev]);
        return id;
    }, [zones]);

    const updateWorkOrder = useCallback((id: string, input: { zone: string; type: WorkOrder['type']; assignee: string; due: string; priority: WorkOrder['priority'] }) => {
        const z = zones.find(zz => zz.id === input.zone) ?? findZone(input.zone);
        setWorkOrders(prev => prev.map(w => (w.id === id ? {
            ...w, zone: input.zone, zoneName: z ? z.name : w.zoneName, type: input.type,
            title: `${z ? z.name : w.zoneName} ${input.type} 작업지시`, assignee: input.assignee,
            due: input.due, priority: input.priority,
        } : w)));
    }, [zones]);

    const addZone = useCallback((input: { name: string; category: ZoneCategory; monitorType: MonitorType; mgr: string; treeName: string; treeCount: number; soilType: string; ph: number; drainage: '양호' | '보통' | '불량'; polygon: LatLng[] }) => {
        const centroid = calcCentroid(input.polygon);
        const areaM2 = Math.round(calcPolygonAreaM2(input.polygon));
        const moist = 55 + Math.floor(Math.random() * 20); // 신규 등록 구역은 초기 관리 직후로 가정(양호~주의 구간)
        const id = `Z${String(++zoneSeqRef.current).padStart(2, '0')}`;
        const zone: Zone = {
            id, name: input.name, category: input.category, monitorType: input.monitorType, district: '신규 등록 구역',
            area: areaM2, treeName: input.treeName || '수종 미입력', treeCount: input.treeCount || 0,
            moist, mgr: input.mgr, lastDate: '2026-07-20', temp: 27, weather: '☀️',
            ph: input.ph, soilType: input.soilType, drainage: input.drainage,
            lat: centroid.lat, lng: centroid.lng, polygon: input.polygon,
            status: moistStatus(moist), icon: treeEmoji(input.treeName),
        };
        setZones(prev => [...prev, zone]);
        return id;
    }, []);

    const startWorkOrder = useCallback((id: string) => {
        setWorkOrders(prev => prev.map(w => (w.id === id ? { ...w, status: 'progress' as WorkStatus } : w)));
    }, []);

    const moveWorkOrderStatus = useCallback((id: string, status: WorkStatus) => {
        setWorkOrders(prev => prev.map(w => (w.id === id ? { ...w, status } : w)));
    }, []);

    const completeWorkOrder = useCallback((id: string, resultNote: string, amt = '0L', mins = '0분') => {
        setWorkOrders(prev => {
            const target = prev.find(w => w.id === id);
            if (target) {
                const now = new Date();
                const completedAt = `2026-07-20 ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                setWorkHistory(h => [{
                    date: completedAt, zone: target.zone, zoneName: target.zoneName, type: target.type,
                    worker: target.assignee, amt, method: '점적관수', mins, before: 30, after: 68,
                    note: resultNote || '작업 완료',
                }, ...h]);
                return prev.map(w => (w.id === id ? { ...w, status: 'done' as WorkStatus, completedAt, result: resultNote || '작업 완료' } : w));
            }
            return prev;
        });
    }, []);

    return (
        <Ctx.Provider value={{
            toasts, toast,
            panel, openPanel, closePanel,
            alerts, markAlertRead, markAllAlertsRead,
            zones, addZone,
            workOrders, createWorkOrder, updateWorkOrder, startWorkOrder, completeWorkOrder, moveWorkOrderStatus,
            workHistory,
        }}>
            {children}
            <div className="sian-toast-wrap">
                {toasts.map(t => (
                    <div key={t.id} className={`sian-toast ${t.type}`}>
                        <span style={{ fontWeight: 800, flexShrink: 0 }}>{ICON[t.type]}</span>
                        <span>{t.msg}</span>
                    </div>
                ))}
            </div>
            <div className={`sian-rpnl-overlay${panel ? ' open' : ''}`} onClick={closePanelFromOverlay} />
            <div className={`sian sian-rpanel${panel ? ' open' : ''}`}>
                {panel && (
                    <>
                        <div className="rpnl-hd">
                            <span className="rpnl-title">{panel.title}</span>
                            <button className="rpnl-close" onClick={closePanel}>✕</button>
                        </div>
                        <div className="rpnl-body">{panel.body}</div>
                        {panel.footer && <div className="rpnl-ft">{panel.footer}</div>}
                    </>
                )}
            </div>
        </Ctx.Provider>
    );
}
